import { useTranslator } from "@eo-locale/react";

import { string, object } from "yup";

import { ChangeEvent, useState, useCallback, useEffect } from "react";

import {
  FiMapPin,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  FormErrorIcon,
  HTMLWrapper,
  AsyncSelect,
  chakraComponents,
  useBreakpointValue,
  DropdownIndicatorProps,
  VisuallyHidden,
  Checkbox
} from "@/storybook-components";

import type { Question, Location, LocationSuggestion } from "@/types";
import { getLocationSuggestions, getLocations } from "@/models";

import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";

interface LocationQuestionProps {
  question: Question;
}

// Initialize the empty location object
const emptyLocation: Location = {
  addressFull: "",
  postcode: 0,
  neighborhood: "",
  district: "",
  city: "",
  subregion: "",
  region: "",
  country: "",
  addressShort: "",
  xCoordinate: "",
  yCoordinate: "",
  xMin: "",
  xMax: "",
  yMin: "",
  yMax: "",
  wkid: 0
};

const locationSchema = object().shape({
  text: string().required("blank").nullable()
});

export const validate = async (
  value: Location,
  question: Question
): Promise<string[]> => {
  //updated validation to support 'Not in country' check
  if (+value?.wkid === 0) {
    return [];
  }
  try {
    question.required && (await locationSchema.validate(value || {}));
    return [];
  } catch (err) {
    return err.errors;
  }
};

const LocationQuestion = ({ question }: LocationQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers, errors, status, country, baseUrl } = useAppSelector(
    (state) => ({
      answers: state.questions.answers,
      errors: state.questions.errors,
      status: state.questions.status,
      country: state.site?.site.country,
      baseUrl: state.adapter.adapter.baseUrl
    })
  );

  const [isNotInLocation, setIsNotInLocation] = useState(false);
  const size = useBreakpointValue({ base: "lg", lg: "md" });

  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);
  const { token } = fetchAuthToken({ user, anonymousUser });

  const answer = answers[question.id] || "";
  const location =
    answer && answer?.wkid !== 0
      ? { label: answer?.text, value: answer?.text, isNotInLocation }
      : null;
  const notInCountryName = `${translate("questions.location.notInCountry", {
    countryName: country
  })}`;

  const error = errors[question.id] || [];

  const handleNoOptionsMessage = (e: { inputValue: string }): string => {
    if (!e.inputValue)
      return translate("questions.region.enterYourPostalDetails");

    return translate("questions.region.noMatchingPostalDetails");
  };

  //This method is to handle the participant's lcoation if they are not present in the country.
  const handleCheckboxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const updatedLocation = event.target.checked
        ? {
            ...emptyLocation,
            addressFull: notInCountryName,
            addressShort: notInCountryName
          }
        : {};

      dispatch(setAnswer({ questionId: question.id, answer: updatedLocation }));
      setIsNotInLocation(event.target.checked);
    },
    [country, question.id]
  );

  const getAddress = async (magicKey: string, text: string) => {
    const type = question.subtype === "FullAddress" ? "full_address" : "postal";
    const address = await getLocations(token, baseUrl, {
      address: text,
      magicKey,
      type
    });

    const newAddress = { ...address[0], text };

    dispatch(
      setAnswer({
        questionId: question.id,
        answer: Object.keys(newAddress).length ? newAddress : {}
      })
    );
  };

  const handleChange = (e): void => {
    getAddress(e?.data?.magicKey, e?.data?.text);
  };

  const fetchLocationData = async (query: string) => {
    const type = question.subtype === "FullAddress" ? "full_address" : "postal";
    return getLocationSuggestions(token, baseUrl, {
      text: query,
      type
    }).then((locations) =>
      locations.map((location: LocationSuggestion) => {
        const { text } = location;

        return {
          label: text,
          value: text,
          data: { ...location }
        };
      })
    );
  };

  //To handle the null state of location, to make it work with the validation as well as empty state of select.
  //Also, since the BE expects only an empty object for unanswered ques.
  const locationValue = () => {
    const notInLocCheck = isNotInLocation || !location?.value;
    return !question.extraSuburb && location?.value
      ? location
      : notInLocCheck
        ? ""
        : location;
  };

  //To set the location state initially in profile, when Not in country is selected during registration.
  useEffect(() => {
    if (answer && answer?.wkid === 0) {
      setIsNotInLocation(true);
    }
  }, [answer]);

  return (
    <FormControl
      my={4}
      id={`${question.id}`}
      isInvalid={error.length > 0}
      isRequired={question.required}
      isDisabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <FormLabel>
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </FormLabel>
      <AsyncSelect
        size={size}
        value={locationValue()}
        isClearable
        menuPlacement="auto"
        maxMenuHeight={200}
        noOptionsMessage={handleNoOptionsMessage}
        innerProps={{ "data-testid": "locationQuestion-asyncSelect" }}
        onChange={handleChange}
        loadOptions={fetchLocationData}
        placeholder={translate("questions.location.selectOption")}
        isDisabled={!question.extraSuburb ? false : isNotInLocation}
        components={{
          DropdownIndicator: (props: DropdownIndicatorProps) => (
            <chakraComponents.DropdownIndicator {...props}>
              <FiMapPin />
            </chakraComponents.DropdownIndicator>
          )
        }}
      />
      {error.length > 0 && (
        <FormErrorMessage data-testid="locationQuestion-errorMessage">
          <FormErrorIcon />
          {translate(`errors.${error}`)}
        </FormErrorMessage>
      )}
      <FormHelperText>{question.notes}</FormHelperText>
      {question.extraSuburb && (
        <Checkbox
          size={{ base: "lg", xl: "md" }}
          isDisabled={false}
          alignItems="center"
          onChange={handleCheckboxChange}
          isChecked={answer?.wkid === 0}
        >
          <HTMLWrapper
            html={translate("questions.location.notInCountry", {
              countryName: country
            })}
          />
        </Checkbox>
      )}
    </FormControl>
  );
};

export default LocationQuestion;
